import { ArrowLeftUniversal, GoogleUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Flex,
  Form,
  Input,
  Select,
  Spin,
  Tag,
  Typography,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DocumentTitle from 'react-document-title';

import {
  GET_UCD_CAMPAIGN,
  UPDATE_UCD_CAMPAIGN,
  getUCDCampaign,
  getUCDConfigs,
  resetUCDCampaign,
  updateUCDCampaign,
} from 'omega-rtc/actions/ucd';
import type { UpdateJourneyCampaignRequest } from 'omega-rtc/actions/ucd';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';
import {
  BUDGET_SOURCE_DISPLAY_NAMES,
  STATUS,
  UCD_ACTION_TYPES,
  UCD_RESOURCE_NAMES,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import { getJourneyCampaignDisplayStatus } from 'omega-rtc/utils/journey-campaign-status';
import { isRequestsPending } from 'omega-rtc/utils/request-state-utils';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import { isValidGoogleSheetUrl } from './CreateJourneyCampaignGroup.utils';
import {
  getJourneyCampaignProductOfferingOptions,
  JOURNEY_CAMPAIGN_VERTICAL_OPTIONS,
} from './JourneyCampaignMetadataOptions';
import './UCDHomePage.scss';

type Props = {
  params: {
    campaignID?: string;
    entityID?: string;
  };
  router: { push: (path: string) => void };
};

type JourneyCampaignEditValues = {
  audienceType: 'PAX' | 'DAX' | 'MEX' | '';
  budgetSource: string;
  campaignName: string;
  countryID: number;
  endDate: Dayjs;
  journeyType: 'SINGLE_TOUCH' | 'MULTI_TOUCH';
  productOffering: string;
  sourceType: 'GOOGLE_SHEET';
  sourceUrl: string;
  startDate: Dayjs;
  vertical: string;
};

export type JourneyCampaignEditDetails = {
  audienceType: 'PAX' | 'DAX' | 'MEX' | '';
  budgetSource: string;
  campaignID: string;
  countryID: number;
  countryName: string;
  endDate: string;
  journeyType: 'SINGLE_TOUCH' | 'MULTI_TOUCH';
  name: string;
  originalCampaign: { [key: string]: any };
  productOffering: string;
  projectID: string;
  sourceType: 'GOOGLE_SHEET';
  sourceUrl: string;
  startDate: string;
  status: string;
  vertical: string;
};

const AUDIENCE_TYPE_OPTIONS = [
  { label: 'Passenger', value: 'PAX' },
  { label: 'DAX', value: 'DAX' },
  { label: 'MEX', value: 'MEX' },
];

const JOURNEY_TYPE_OPTIONS = [
  { label: 'Single Touch', value: 'SINGLE_TOUCH' },
  { label: 'Multi Touch', value: 'MULTI_TOUCH' },
];

const getAudienceTypeValue = (audienceType = ''): 'PAX' | 'DAX' | 'MEX' | '' => {
  const normalizedAudienceType = audienceType.trim().toUpperCase();
  if (normalizedAudienceType === 'DRIVER' || normalizedAudienceType === 'DAX') {
    return 'DAX';
  }
  if (normalizedAudienceType === 'FOODMAX' || normalizedAudienceType === 'MEX') {
    return 'MEX';
  }
  if (normalizedAudienceType === 'PASSENGER' || normalizedAudienceType === 'PAX') {
    return 'PAX';
  }
  return '';
};

const getJourneyTypeValue = (journeyType = ''): 'SINGLE_TOUCH' | 'MULTI_TOUCH' => (
  journeyType.trim().toUpperCase().includes('MULTI') ? 'MULTI_TOUCH' : 'SINGLE_TOUCH'
);

const normalizeStatus = (status = '') => status.trim().toUpperCase().replace(/\s+/g, '_');

const getDisplayStatus = (status: string) => {
  const normalizedStatus = normalizeStatus(status);
  if (normalizedStatus === STATUS.pendingApproval) return 'Pending Approval';
  return normalizedStatus.toLowerCase()
    .split('_')
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
};

const getStatusColor = (status: string) => {
  const normalizedStatus = normalizeStatus(status);
  if (normalizedStatus === 'DRAFT') return 'warning';
  if (normalizedStatus === 'DEACTIVATED') return 'default';
  if (normalizedStatus === 'ACTIVE') return 'success';
  return 'processing';
};

export const isJourneyCampaignEditableStatus = (status: string) => (
  ['DRAFT', 'DEACTIVATED'].includes(normalizeStatus(status))
);

export const getJourneyCampaignEditDetails = (
  campaignResponse: any,
  expectedCampaignID = '',
  countries: Array<{ countryID: number; name?: string; code?: string }> = [],
): JourneyCampaignEditDetails | undefined => {
  if (!campaignResponse) return undefined;

  const campaign = _.get(campaignResponse, 'campaign', campaignResponse) || {};
  const campaignID = campaignResponse.campaignID || campaign.campaignID || campaign.ID || '';
  if (!campaignID || (expectedCampaignID && campaignID !== expectedCampaignID)) {
    return undefined;
  }

  const journeyMetadata = campaign.journeyMetadata || campaignResponse.journeyMetadata || {};
  const source = journeyMetadata.source || {};
  const countryID = Number(campaignResponse.countryID || campaign.countryID || 0);
  const country = countries.find((candidate) => (
    Number(candidate.countryID) === countryID
  ));

  return {
    audienceType: getAudienceTypeValue(
      campaignResponse.audienceType
      || campaignResponse.audience
      || campaign.audienceType
      || campaign.audience,
    ),
    budgetSource: campaign.budgetSource || campaignResponse.budgetSource || '',
    campaignID,
    countryID,
    countryName: campaignResponse.countryName
      || campaign.countryName
      || (country && country.name)
      || campaignResponse.countryCode
      || (country && country.code)
      || '—',
    endDate: campaign.endDate || campaign.end || campaignResponse.endDate || '',
    journeyType: getJourneyTypeValue(campaign.journeyType || campaignResponse.journeyType),
    name: campaign.name || campaign.code || campaignID,
    originalCampaign: campaign,
    productOffering: campaign.productOffering
      || campaign.CVP
      || campaignResponse.productOffering
      || campaignResponse.CVP
      || '',
    projectID: campaignResponse.projectID || campaign.projectID || '',
    sourceType: 'GOOGLE_SHEET',
    sourceUrl: source.url || '',
    startDate: campaign.startDate || campaign.start || campaignResponse.startDate || '',
    status: getJourneyCampaignDisplayStatus(campaignResponse),
    vertical: campaign.vertical
      || _.get(campaign, 'verticals[0]')
      || campaignResponse.vertical
      || _.get(campaignResponse, 'verticals[0]')
      || '',
  };
};

export const buildJourneyCampaignUpdateRequest = (
  values: JourneyCampaignEditValues,
  originalCampaign: { [key: string]: any },
  user: string,
): UpdateJourneyCampaignRequest => ({
  campaign: {
    ...originalCampaign,
    budgetSource: values.budgetSource,
    CVP: values.productOffering,
    end: values.endDate.format('YYYY-MM-DD'),
    journeyMetadata: {
      ...(originalCampaign.journeyMetadata || {}),
      source: {
        ..._.get(originalCampaign, 'journeyMetadata.source', {}),
        type: 'GOOGLE_SHEET',
        url: values.sourceUrl,
      },
    },
    journeyType: values.journeyType,
    name: values.campaignName,
    start: values.startDate.format('YYYY-MM-DD'),
    verticals: [
      values.vertical,
      ..._.get(originalCampaign, 'verticals', [])
        .slice(1)
        .filter((vertical) => vertical !== values.vertical),
    ],
  },
  user,
});

export const JourneyCampaignEditForm = ({
  budgetSources = [],
  detail,
  isSubmitting = false,
  onCancel,
  onSubmit,
}: {
  budgetSources?: string[];
  detail: JourneyCampaignEditDetails;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (values: JourneyCampaignEditValues) => void;
}) => {
  const [form] = Form.useForm();
  const isEditable = isJourneyCampaignEditableStatus(detail.status);
  const selectedVertical = Form.useWatch('vertical', form) || detail.vertical;
  const productOfferingOptions = getJourneyCampaignProductOfferingOptions(selectedVertical);
  const budgetSourceOptions = budgetSources.map((budgetSource) => ({
    label: _.get(BUDGET_SOURCE_DISPLAY_NAMES, budgetSource, budgetSource),
    value: budgetSource,
  }));
  if (detail.budgetSource && !budgetSourceOptions.some(
    (option) => option.value === detail.budgetSource,
  )) {
    budgetSourceOptions.push({
      label: _.get(
        BUDGET_SOURCE_DISPLAY_NAMES,
        detail.budgetSource,
        detail.budgetSource,
      ),
      value: detail.budgetSource,
    });
  }
  if (detail.productOffering && !productOfferingOptions.some(
    (option) => option.value === detail.productOffering,
  )) {
    productOfferingOptions.push({
      label: detail.productOffering,
      value: detail.productOffering,
    });
  }

  return (
    <DocumentTitle title={`Edit ${detail.name} - Holos`}>
      <div className="ucd-basic journey-campaign-country-edit-page">
        <Button icon={<ArrowLeftUniversal />} onClick={onCancel} type="text">
          Campaign details
        </Button>

        <Flex
          align="center"
          gap="12px"
          justify="space-between"
          wrap="wrap"
          style={{ marginBottom: '24px' }}
        >
          <div>
            <Typography.Title level={2} style={{ marginBottom: '4px' }}>
              Edit Journey Campaign
            </Typography.Title>
            <Typography.Text type="secondary">
              Update the campaign setup and Google Sheet source.
            </Typography.Text>
          </div>
          <Tag color={getStatusColor(detail.status)}>
            {getDisplayStatus(detail.status)}
          </Tag>
        </Flex>

        <Form
          className="journey-campaign-country-edit-form"
          form={form}
          initialValues={{
            audienceType: detail.audienceType,
            budgetSource: detail.budgetSource,
            campaignName: detail.name,
            countryID: detail.countryID,
            endDate: dayjs(detail.endDate),
            journeyType: detail.journeyType,
            productOffering: detail.productOffering,
            sourceType: detail.sourceType,
            sourceUrl: detail.sourceUrl,
            startDate: dayjs(detail.startDate),
            vertical: detail.vertical,
          }}
          layout="vertical"
          onFinish={onSubmit}
        >
          {!isEditable && (
            <Alert
              message="Only Draft or Deactivated campaigns can be edited."
              showIcon
              style={{ marginBottom: '16px' }}
              type="warning"
            />
          )}

          <Card title="Campaign details" style={{ marginBottom: '20px' }}>
            <div className="journey-campaign-country-edit-grid">
              <Form.Item
                label="Campaign name"
                name="campaignName"
                rules={[{ required: true, message: 'Please input the campaign name' }]}
              >
                <Input disabled={!isEditable} placeholder="Enter campaign name" />
              </Form.Item>
              <Form.Item label="Country" name="countryID">
                <Select
                  disabled
                  options={[{ label: detail.countryName, value: detail.countryID }]}
                />
              </Form.Item>
              <Form.Item label="Audience type" name="audienceType">
                <Select disabled options={AUDIENCE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                label="Journey type"
                name="journeyType"
                rules={[{ required: true, message: 'Please select a journey type' }]}
              >
                <Select disabled={!isEditable} options={JOURNEY_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                label="Vertical"
                name="vertical"
                rules={[{ required: true, message: 'Please select a vertical' }]}
              >
                <Select
                  disabled={!isEditable}
                  options={JOURNEY_CAMPAIGN_VERTICAL_OPTIONS}
                  placeholder="Select vertical"
                  onChange={() => form.setFieldsValue({ productOffering: undefined })}
                />
              </Form.Item>
              <Form.Item
                label="Product offering"
                name="productOffering"
                rules={[{ required: true, message: 'Please select a product offering' }]}
              >
                <Select
                  disabled={!isEditable || !selectedVertical}
                  options={productOfferingOptions}
                  placeholder={selectedVertical
                    ? 'Select product offering'
                    : 'Select a vertical first'}
                />
              </Form.Item>
              <Form.Item
                label="Budget source"
                name="budgetSource"
                rules={[{ required: true, message: 'Please select a budget source' }]}
              >
                <Select
                  disabled={!isEditable}
                  options={budgetSourceOptions}
                  placeholder="Select budget source"
                />
              </Form.Item>
              <Form.Item
                label="Start date"
                name="startDate"
                rules={[{ required: true, message: 'Please select the start date' }]}
              >
                <DatePicker disabled={!isEditable} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                dependencies={['startDate']}
                label="End date"
                name="endDate"
                rules={[
                  { required: true, message: 'Please select the end date' },
                  ({ getFieldValue }) => ({
                    validator(_, endDate) {
                      const startDate = getFieldValue('startDate');
                      if (!endDate || !startDate || !endDate.isBefore(startDate)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('End date cannot be before the start date'));
                    },
                  }),
                ]}
              >
                <DatePicker disabled={!isEditable} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </Card>

          <Card
            title={(
              <Flex align="center" gap="8px">
                <GoogleUniversal aria-hidden="true" />
                <span>Google Sheet source</span>
              </Flex>
            )}
          >
            <Form.Item label="Source" name="sourceType">
              <Select
                disabled
                options={[{ label: 'Google Sheet', value: 'GOOGLE_SHEET' }]}
              />
            </Form.Item>
            <Form.Item
              label="Source URL"
              name="sourceUrl"
              rules={[
                { required: true, message: 'Please input the Google Sheet URL' },
                {
                  validator: (_, value) => (isValidGoogleSheetUrl(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please input a valid Google Sheet URL'))),
                },
              ]}
            >
              <Input
                disabled={!isEditable}
                placeholder="https://docs.google.com/spreadsheets/..."
              />
            </Form.Item>
            <Flex gap="8px" justify="flex-end">
              <Button onClick={onCancel}>Cancel</Button>
              <Button
                disabled={!isEditable}
                htmlType="submit"
                loading={isSubmitting}
                type="primary"
              >
                Save changes
              </Button>
            </Flex>
          </Card>
        </Form>
      </div>
    </DocumentTitle>
  );
};

export const JourneyCampaignEditPage = ({ params, router }: Props) => {
  const dispatch = useDispatch();
  const projectID = params.entityID || '';
  const campaignID = params.campaignID || '';
  const campaignResponse = useSelector((state: any) => state.ucd.campaignView);
  const budgetSources = useSelector((state: any) => _.get(
    state,
    'ucd.configs.budgetSources',
    [],
  ));
  const countries = useSelector((state: any) => state.countries.entities || []);
  const user = useSelector((state: any) => state.app.user) || {};
  const getRequest = useSelector((state: any) => _.get(
    state.requests,
    getRequestStateKey(GET_UCD_CAMPAIGN),
    {},
  ));
  const updateRequest = useSelector((state: any) => _.get(
    state.requests,
    getRequestStateKey(UPDATE_UCD_CAMPAIGN),
    {},
  ));
  const previousUpdateRequest = useRef(updateRequest);
  const detail = getJourneyCampaignEditDetails(campaignResponse, campaignID, countries);
  const detailPath = UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_VIEW(projectID, campaignID);

  useEffect(() => {
    if (projectID && campaignID) {
      dispatch(getUCDCampaign(projectID, campaignID));
    }
    dispatch(getUCDConfigs());
    return () => { dispatch(resetUCDCampaign()); };
  }, [campaignID, dispatch, projectID]);

  useEffect(() => {
    const wasPending = previousUpdateRequest.current.isPending;
    previousUpdateRequest.current = updateRequest;
    if (!wasPending) return;

    if (updateRequest.isSuccessful) {
      snackBar.success({ content: 'Journey Campaign updated successfully' });
      router.push(detailPath);
    } else if (updateRequest.isFailure) {
      snackBar.error({
        content: _.get(
          updateRequest,
          'error.response.data.message',
          'Unable to update Journey Campaign',
        ),
      });
    }
  }, [detailPath, router, updateRequest]);

  if (getRequest.isFailure) {
    return <Alert message="Journey Campaign could not be loaded" showIcon type="error" />;
  }
  if (isRequestsPending(getRequest) || !detail) {
    return <Flex align="center" justify="center" style={{ minHeight: 320 }}><Spin /></Flex>;
  }

  return (
    <JourneyCampaignEditForm
      budgetSources={budgetSources}
      detail={detail}
      isSubmitting={updateRequest.isPending}
      onCancel={() => router.push(detailPath)}
      onSubmit={(values) => dispatch(updateUCDCampaign(
        buildJourneyCampaignUpdateRequest(values, detail.originalCampaign, user.email || ''),
        projectID,
        campaignID,
      ))}
    />
  );
};

const AuthorizedJourneyCampaignEditPage = (props: Props) => (
  <ShowIfAuthorized
    conditions={[{ resource: UCD_RESOURCE_NAMES.campaign, action: UCD_ACTION_TYPES.update }]}
    unauthorizedContent={(
      <Alert message="You don't have access to edit Holos campaign" showIcon type="error" />
    )}
  >
    <JourneyCampaignEditPage {...props} />
  </ShowIfAuthorized>
);

export default AuthorizedJourneyCampaignEditPage;
